import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card } from "./ui/card";
import { MessageCircle, X, Send, Loader2, Download, Copy, ImageIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useChatbotConfig } from "@/hooks/useChatbotConfig";
import logoImage from "@/assets/logo.png";
import { trackEvent } from "@/hooks/usePageTracking";

interface Message {
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  image?: {
    url: string;
    prompt: string;
    description?: string;
  };
}

export const Chatbot = () => {
  const { t, i18n } = useTranslation();
  const { config } = useChatbotConfig();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random()}`);
  const [showWelcome, setShowWelcome] = useState(false);
  const [imageGenerating, setImageGenerating] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Show welcome message after 10 seconds
    const timer = setTimeout(() => {
      if (!isOpen && messages.length === 0) {
        setShowWelcome(true);
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [isOpen, messages.length]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    // Load conversation from localStorage
    const savedConversationId = localStorage.getItem("chatbot_conversation_id");
    const savedMessages = localStorage.getItem("chatbot_messages");
    
    if (savedConversationId) {
      setConversationId(savedConversationId);
    }
    
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages);
        setMessages(parsed.map((m: any) => ({ ...m, timestamp: new Date(m.timestamp) })));
      } catch (e) {
        console.error("Failed to parse saved messages:", e);
      }
    }
  }, []);

  const saveToLocalStorage = (convId: string, msgs: Message[]) => {
    try {
      // Keep only the last 20 messages and strip images to prevent quota exceeded errors
      const messagesToSave = msgs.slice(-20).map(msg => {
        // Remove image data from messages to save space
        if (msg.image) {
          return {
            ...msg,
            image: undefined, // Don't save images in localStorage
          };
        }
        return msg;
      });
      
      localStorage.setItem("chatbot_conversation_id", convId);
      localStorage.setItem("chatbot_messages", JSON.stringify(messagesToSave));
    } catch (error) {
      // If quota exceeded, clear all messages and start fresh
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn("LocalStorage quota exceeded, clearing chat history");
        localStorage.removeItem("chatbot_messages");
        localStorage.removeItem("chatbot_conversation_id");
        
        // Show user-friendly message
        const language = i18n.language;
        toast({
          title: language === 'dk' ? "Chat ryddet" : "Chat cleared",
          description: language === 'dk' 
            ? "Din chat historik er blevet ryddet for at frigøre plads. Du kan fortsætte chatten."
            : "Your chat history has been cleared to free up space. You can continue chatting.",
        });
      } else {
        // Other storage errors
        console.error("Storage error:", error);
      }
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      role: "user",
      content: input,
      timestamp: new Date(),
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("chat-with-clara", {
        body: {
          message: input,
          conversationId,
          sessionId,
          language: i18n.language,
        },
      });

      if (error) throw error;

      // Add logo to image if present
      let imageWithLogo = data.image;
      if (data.image?.url) {
        try {
          const brandedImageUrl = await addLogoToImage(data.image.url);
          imageWithLogo = {
            ...data.image,
            url: brandedImageUrl,
          };
        } catch (error) {
          console.warn('Failed to add logo to image:', error);
          // Continue with original image if logo overlay fails
        }
      }

      const assistantMessage: Message = {
        role: "assistant",
        content: data.message,
        timestamp: new Date(),
        image: imageWithLogo ? {
          url: imageWithLogo.url,
          prompt: imageWithLogo.prompt,
          description: imageWithLogo.description,
        } : undefined,
      };

      const updatedMessages = [...newMessages, assistantMessage];
      setMessages(updatedMessages);
      
      if (data.conversationId && !conversationId) {
        setConversationId(data.conversationId);
        saveToLocalStorage(data.conversationId, updatedMessages);
      } else {
        saveToLocalStorage(conversationId || data.conversationId, updatedMessages);
      }
    } catch (error: any) {
      console.error("Chat error:", error);
      toast({
        title: t("error", { defaultValue: "Error" }),
        description: error.message || "Failed to send message",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setConversationId(null);
    localStorage.removeItem("chatbot_conversation_id");
    localStorage.removeItem("chatbot_messages");
  };

  const downloadImage = (imageUrl: string, prompt: string) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `fraimeworks-${prompt.slice(0, 30).replace(/\s+/g, '-')}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({
      title: i18n.language === "dk" ? "Billede downloadet" : "Image downloaded",
      description: i18n.language === "dk" ? "Billedet er gemt" : "Image has been saved",
    });
  };

  const copyImageToClipboard = async (imageUrl: string) => {
    try {
      // For base64 images
      if (imageUrl.startsWith('data:image')) {
        const blob = await fetch(imageUrl).then(r => r.blob());
        await navigator.clipboard.write([
          new ClipboardItem({ [blob.type]: blob })
        ]);
        toast({
          title: i18n.language === "dk" ? "Billede kopieret" : "Image copied",
          description: i18n.language === "dk" ? "Billedet er kopieret til udklipsholderen" : "Image copied to clipboard",
        });
      }
    } catch (error) {
      console.error("Copy error:", error);
      toast({
        title: i18n.language === "dk" ? "Fejl" : "Error",
        description: i18n.language === "dk" ? "Kunne ikke kopiere billedet" : "Couldn't copy image",
        variant: "destructive",
      });
    }
  };

  const addLogoToImage = async (imageUrl: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Set canvas size to image size
        canvas.width = img.width;
        canvas.height = img.height;

        // Draw the original image
        ctx.drawImage(img, 0, 0);

        // Load and draw logo
        const logo = new Image();
        logo.crossOrigin = "anonymous";
        
        logo.onload = () => {
          // Calculate logo size (10% of image width, max 150px)
          const logoWidth = Math.min(img.width * 0.1, 150);
          const logoHeight = (logo.height / logo.width) * logoWidth;
          
          // Position in bottom-right corner with 20px padding
          const x = canvas.width - logoWidth - 20;
          const y = canvas.height - logoHeight - 20;
          
          // Add semi-transparent white background for logo
          ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
          ctx.fillRect(x - 10, y - 10, logoWidth + 20, logoHeight + 20);
          
          // Draw logo
          ctx.drawImage(logo, x, y, logoWidth, logoHeight);
          
          // Convert canvas to base64
          resolve(canvas.toDataURL('image/png'));
        };
        
        logo.onerror = () => {
          // If logo fails to load, return original image
          console.warn('Logo failed to load, using original image');
          resolve(imageUrl);
        };
        
        // Load logo from assets
        logo.src = logoImage;
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = imageUrl;
    });
  };

  const welcomeText = i18n.language === "dk" 
    ? (config?.welcome_text_dk || "Hej! 👋 Har du spørgsmål om visuel facilitering?")
    : (config?.welcome_text_en || "Hi! 👋 Questions about visual facilitation?");
  
  const chatbotName = i18n.language === "dk"
    ? (config?.name_dk || "Charlotte")
    : (config?.name_en || "Charlotte");
  
  const chatbotTitle = i18n.language === "dk"
    ? (config?.title_dk || "FraimeWorks Assistent")
    : (config?.title_en || "FraimeWorks Assistant");
  
  const firstMessage = i18n.language === "dk"
    ? (config?.first_message_dk || "Hej! Jeg er Charlotte. Hvordan kan jeg hjælpe dig i dag?")
    : (config?.first_message_en || "Hi! I'm Charlotte. How can I help you today?");

  return (
    <>
      {/* Welcome bubble */}
      {showWelcome && !isOpen && (
        <div className="fixed bottom-24 right-6 z-50 animate-in slide-in-from-bottom-5">
          <Card className="p-4 max-w-xs shadow-lg">
            <p className="text-sm">{welcomeText}</p>
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-2 right-2 h-6 w-6 p-0"
              onClick={() => setShowWelcome(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </Card>
        </div>
      )}

      {/* Chat window */}
      {isOpen && (
        <Card className={`fixed bottom-24 right-6 z-50 flex flex-col shadow-2xl animate-in slide-in-from-bottom-5 transition-all duration-300 ${
          messages.some(m => m.image) 
            ? 'w-[600px] max-w-[calc(100vw-3rem)] h-[700px]'
            : 'w-96 max-w-[calc(100vw-3rem)] h-[500px]'
        }`}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-primary text-primary-foreground rounded-t-lg">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{chatbotName}</h3>
                  <span className="text-[10px] bg-primary-foreground/20 px-2 py-0.5 rounded-full">
                    🎨 AI + {i18n.language === "dk" ? "Billeder" : "Images"}
                  </span>
                </div>
                <p className="text-xs opacity-90">{chatbotTitle}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="h-8 w-8 p-0 hover:bg-primary-foreground/20"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="space-y-4 pt-4">
                <div className="text-center text-muted-foreground text-sm">
                  {firstMessage}
                </div>
                
                {/* Quick Reply Buttons */}
                <div className="flex flex-col gap-2 px-2">
                  {config?.quick_reply_1_label_dk && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setInput(
                        i18n.language === "dk" 
                          ? config.quick_reply_1_prompt_dk || ""
                          : config.quick_reply_1_prompt_en || ""
                      )}
                      className="w-full justify-start text-xs"
                    >
                      <ImageIcon className="h-3 w-3 mr-2" />
                      {i18n.language === "dk" ? config.quick_reply_1_label_dk : config.quick_reply_1_label_en}
                    </Button>
                  )}
                  
                  {config?.quick_reply_2_label_dk && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setInput(
                        i18n.language === "dk" 
                          ? config.quick_reply_2_prompt_dk || ""
                          : config.quick_reply_2_prompt_en || ""
                      )}
                      className="w-full justify-start text-xs"
                    >
                      <ImageIcon className="h-3 w-3 mr-2" />
                      {i18n.language === "dk" ? config.quick_reply_2_label_dk : config.quick_reply_2_label_en}
                    </Button>
                  )}
                  
                  {config?.quick_reply_3_label_dk && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setInput(
                        i18n.language === "dk" 
                          ? config.quick_reply_3_prompt_dk || ""
                          : config.quick_reply_3_prompt_en || ""
                      )}
                      className="w-full justify-start text-xs"
                    >
                      {i18n.language === "dk" ? config.quick_reply_3_label_dk : config.quick_reply_3_label_en}
                    </Button>
                  )}
                </div>
              </div>
            )}
            
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted"
                  }`}
                >
                  {message.image && (
                    <div className="mb-3">
                      <div className="relative group">
                        <img 
                          src={message.image.url} 
                          alt={message.image.description || "Generated image"}
                          className="rounded-lg max-w-full h-auto"
                        />
                        <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            size="sm"
                            variant="secondary"
                            className="h-8 w-8 p-0"
                            onClick={() => downloadImage(message.image!.url, message.image!.prompt)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="h-8 w-8 p-0"
                            onClick={() => copyImageToClipboard(message.image!.url)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  <p className="text-xs opacity-70 mt-1">
                    {message.timestamp.toLocaleTimeString(i18n.language, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-lg p-3 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">
                    {imageGenerating 
                      ? (i18n.language === "dk" ? "Genererer billede..." : "Generating image...")
                      : (i18n.language === "dk" ? "Skriver..." : "Typing...")
                    }
                  </span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t">
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setInput(i18n.language === "dk" ? "Generer et billede af " : "Generate an image of ")}
                disabled={isLoading}
                className="shrink-0"
                title={i18n.language === "dk" ? "Jeg kan lave billeder! 🎨" : "I can create images! 🎨"}
              >
                <ImageIcon className="h-4 w-4" />
              </Button>
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={
                  i18n.language === "dk"
                    ? "Skriv din besked..."
                    : "Type your message..."
                }
                disabled={isLoading}
                className="flex-1"
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                size="icon"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearChat}
                className="w-full mt-2 text-xs"
              >
                {i18n.language === "dk" ? "Ryd chat" : "Clear chat"}
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Floating button */}
      <Button
        onClick={() => {
          const wasOpen = isOpen;
          setIsOpen(!isOpen);
          setShowWelcome(false);
          
          // Track chatbot open
          if (!wasOpen) {
            trackEvent('chatbot_opened');
          }
        }}
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg z-40"
        size="icon"
        aria-label={isOpen 
          ? (i18n.language === "dk" ? "Luk chat" : "Close chat")
          : (i18n.language === "dk" ? "Åbn chat" : "Open chat")
        }
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <MessageCircle className="h-6 w-6" />
        )}
      </Button>
    </>
  );
};